import React, { FC, useEffect, useState } from 'react'
import { browserName, isMobile } from 'react-device-detect'
import { useTranslate } from 'react-polyglot'
import { Button, Typography } from '@getgo/chameleon-web-react-wrapper'
import { Grid } from '@material-ui/core'
import { Dialog, DialogActions } from '@ucc/unified-admin-common'
import mixpanel from 'mixpanel-browser'

import { useAccountContext } from 'components/contexts/AccountContext'
import { useConnectedUserContext } from 'components/contexts/ConnectedUserContext'
import { useLiveJobsContext } from 'components/contexts/LiveJobsContext'
import { config } from 'config'
import { PBX } from 'types/Pbx'
import { fetchWithToken, mixpanelEventNames } from 'utils'
import { convertToAnalytics, getIsEmployee, getUserAdminRoles } from 'utils/analytics'
import { QUALTRICS_SURVEY_TRIGGER_TYPES } from 'utils/constants'
import { triggerQualtricsSurvey } from 'utils/qualtricsSurvey'

export interface SwitchBackToOldAdminDialogProps {
  onClose: () => void
  pbx: PBX | null
  open: boolean
}

const SwitchBackToOldAdminDialog: FC<SwitchBackToOldAdminDialogProps> = ({ onClose, pbx, open }) => {
  const { accountKey, licensesDetails } = useAccountContext()
  const t = useTranslate()
  const liveJobs = useLiveJobsContext()
  const iAm = useConnectedUserContext()
  const [accountSwitchback, setAccountSwitchback] = useState(false)

  useEffect(() => {
    if (open) {
      triggerQualtricsSurvey(QUALTRICS_SURVEY_TRIGGER_TYPES.switchback, null)
    }
  }, [open])

  useEffect(() => {
    if (accountSwitchback && liveJobs?.type === 'updateaccount' && liveJobs?.status === 'COMPLETED') {
      const jobResponse = JSON.parse(liveJobs.request.content)
      if (jobResponse && !jobResponse.unifiedAdmin && jobResponse.goToAdminPreview) {
        onClose()
        window.location.assign(`${config.EXTERNAL_ADMIN_PORTAL_URL}/#accounts/${accountKey}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveJobs?.type, liveJobs?.status, accountSwitchback, onClose, accountKey])

  const switchBack = async () => {
    if (accountKey && iAm) {
      const isEmployee = getIsEmployee(iAm)
      const userAdminRolesOnAccount = getUserAdminRoles(iAm.accounts, accountKey)
      const analyticsIam = convertToAnalytics({
        ...iAm,
        adminRoles: userAdminRolesOnAccount,
        isEmployee,
      })

      mixpanel.track(mixpanelEventNames.APP_SWITCH_BACK, {
        Action: 'Switch back event to External Admin Center',
        ...analyticsIam,
        licenses: licensesDetails?.results,
        products: licensesDetails?.products,
        accountKey,
        region: pbx?.region,
        language: pbx?.language,
        billingGroupsEnabled: pbx?.billingGroupsEnabled,
        billedByLicense: pbx?.billedByLicense,
        userAgent: navigator.userAgent,
        browserName,
        isMobile,
      })
    }

    const promise = {
      method: 'PUT',
      baseUrl: config.EXTERNAL_ADMIN_URL,
      path: '',
      useReqPath: false,
      useV5: false,
      useVersion: '',
      resource: `ext-admin/rest/accounts/${accountKey}`,
      options: {
        body: JSON.stringify({
          unifiedAdmin: false,
          goToAdminPreview: true,
        }),
      },
    }

    try {
      const response = await fetchWithToken(promise)
      if (response.ok) setAccountSwitchback(true)
    } catch (err: any) {
      console.error(err.toString())
    }
  }

  return (
    <Dialog
      size="sm"
      title={t('Switch back to legacy Admin Center')}
      onClose={onClose}
      open={open}
      disableEnforceFocus={true}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <Typography>
            {t(
              "You'll be temporarily switched back to the legacy Admin Center. In the coming months all users will be switched to GoTo Admin.",
            )}
          </Typography>
        </Grid>
        <DialogActions>
          <Button variant="neutral" data-testid="switch-back-to-old-admin-cancel-btn" onClick={onClose}>
            {t('Keep GoTo Admin')}
          </Button>
          <Button data-testid="switch-back-to-old-admin-btn" variant="primary" onClick={() => switchBack()}>
            {t('Switch back')}
          </Button>
        </DialogActions>
      </Grid>
    </Dialog>
  )
}

export default SwitchBackToOldAdminDialog
